import { Box, Card, CardContent, Grid, Paper, Typography, Button } from '@mui/material';
import MessageIcon from '@mui/icons-material/Message';
import CampaignIcon from '@mui/icons-material/Campaign';
import Timeline from '@mui/icons-material/Timeline';
import { TimeAgo } from './time-ago';
import { UserActionsMenu } from './user-actions-menu';
import { SessionStatusPill } from './session-status-pill';
import { SubscriptionStatusChip } from './subscription-status-chip';
import { ConnectedEnginesSupport } from './connected-engines-support';
import { Workspace } from '@common/models/workspace';
import moment from 'moment';

interface UserOverviewHeaderProps {
    supportedUserId: string | null;
    user?: {
        email?: string;
        createdAt?: string | Date | moment.Moment;
    };
    currentWorkspace?: Workspace;
    latestSession?: {
        status?: string | null;
    };
    latestSubscription?: any;
    totalMessages: number;
    totalCampaigns: number;
}

const formatTimestamp = (timestamp: string | Date | moment.Moment | undefined): string => {
    if (!timestamp) return '';
    if (moment.isMoment(timestamp)) {
        return timestamp.toISOString();
    }
    if (timestamp instanceof Date) {
        return timestamp.toISOString();
    }
    return timestamp;
};

const getBillingDuration = (subscription: any) => {
    if (subscription.type === 'chargebee') {
        const [, , interval] = (subscription.plan_id as string).split('-');
        return interval === 'Monthly' ? 'Monthly' : 'Annual';
    }

    if (subscription.plan?.intervalUnit === 'year') {
        return 'Annual';
    }
    if (subscription.plan?.intervalUnit === 'month') {
        return 'Monthly';
    }

    return subscription.plan?.intervalUnit || 'N/A';
};

const getPlanName = (subscription: any) => {
    if (subscription.type === 'chargebee') {
        const [planName] = (subscription.plan_id as string).split('-');
        return planName;
    }
    return subscription.plan?.planType || 'N/A';
};

export function UserOverviewHeader({
    supportedUserId,
    user,
    currentWorkspace,
    latestSession,
    latestSubscription,
    totalMessages,
    totalCampaigns
}: UserOverviewHeaderProps) {
    return (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Grid container spacing={4}>
                {/* User Information Section */}
                <Grid item xs={12} lg={7}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        User Overview
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box>
                                <Typography variant="h6" sx={{ mb: 0.5 }}>
                                    {user?.email}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        ID: {supportedUserId}
                                    </Typography>
                                    {user?.createdAt && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                User since:
                                            </Typography>
                                            <TimeAgo timestamp={formatTimestamp(user.createdAt)} showDate={true} />
                                        </Box>
                                    )}
                                    {currentWorkspace && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Workspace:
                                            </Typography>
                                            <Typography variant="body2">
                                                {currentWorkspace.name}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <UserActionsMenu userId={supportedUserId ?? ''} currentEmail={user?.email} />
                        </Box>
                        {latestSession && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Session Status:
                                </Typography>
                                <SessionStatusPill status={latestSession.status} />
                            </Box>
                        )}
                        {latestSubscription && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Subscription:
                                </Typography>
                                <SubscriptionStatusChip status={latestSubscription.status} />
                                <Typography variant="body2">
                                    {getPlanName(latestSubscription)} ({getBillingDuration(latestSubscription)})
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<Timeline />}
                                onClick={() => {
                                    const url = new URL(`/session-timeline/${supportedUserId}`, window.location.origin);
                                    window.open(url.toString(), '_blank');
                                }}
                                size="small"
                            >
                                View Timeline
                            </Button>
                        </Box>
                    </Box>
                </Grid>

                {/* Connected Engines Section */}
                <Grid item xs={12} lg={5}>
                    <ConnectedEnginesSupport userId={supportedUserId ?? ''} />
                </Grid>

                {/* Summary Cards */}
                <Grid item xs={12} sm={6}>
                    <Card>
                        <CardContent>
                            <MessageIcon color="primary" sx={{ mb: 1, fontSize: 30 }} />
                            <Typography variant="h6">Total Messages</Typography>
                            <Typography variant="h4">{totalMessages}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Card>
                        <CardContent>
                            <CampaignIcon color="primary" sx={{ mb: 1, fontSize: 30 }} />
                            <Typography variant="h6">Active Campaigns</Typography>
                            <Typography variant="h4">{totalCampaigns}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Paper>
    );
} 